import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Box,
  Chip,
  Link,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import { formatLinkPostedDate } from '../utils/googleDateUtils';
import { EXTERNAL_LINK_REL, stripTrackingParams } from '../utils/externalUrlUtils';
import CompanyResearchDialog from './CompanyResearchDialog';

const LOCATION_OPTIONS = [
  'USA',
  'Canada',
  'LATAM',
  'Europe',
  'Asia',
  'Australia/Pacific',
  'Africa',
  'Anonymous',
];

const BEHAVIOR_OPTIONS = [
  'Remote Job',
  'Remote/Hybrid',
  'Non Remote Job',
  'Email Found',
  'Login Required',
  'Verification Required',
  'Other Relevant',
  'Expired',
  'Irrelevant',
  'Uncategorized',
  'Invalid URL',
];

// Tag color function (copied from BidLinks.jsx)
const getTagColor = (tag) => {
  switch (tag) {
    case "Email Found":
      return {
        sx: {
          backgroundColor: (theme) =>
            theme.palette.mode === "dark" ? "#1b5e20" : "#4caf50",
          color: "#fff",
        },
      };
    case "Remote Job":
      return {
        sx: {
          backgroundColor: (theme) =>
            theme.palette.mode === "dark" ? "#1565c0" : "#1976d2",
          color: "#fff",
        },
      };
    case "Remote/Hybrid":
      return {
        sx: {
          backgroundColor: (theme) =>
            theme.palette.mode === "dark" ? "#00695c" : "#00897b",
          color: "#fff",
        },
      };
    case "Non Remote Job":
      return {
        sx: {
          backgroundColor: (theme) =>
            theme.palette.mode === "dark" ? "#e65100" : "#ff9800",
          color: "#fff",
        },
      };
    case "Verification Required":
      return {
        sx: {
          backgroundColor: (theme) =>
            theme.palette.mode === "dark" ? "#b71c1c" : "#f44336",
          color: "#fff",
        },
      };
    case "Login Required":
      return {
        sx: {
          backgroundColor: (theme) =>
            theme.palette.mode === "dark" ? "#b71c1c" : "#f44336",
          color: "#fff",
        },
      };
    case "Other Relevant":
      return {
        sx: {
          backgroundColor: (theme) =>
            theme.palette.mode === "dark" ? "#01579b" : "#03a9f4",
          color: "#fff",
        },
      };
    case "Irrelevant":
      return {
        sx: {
          backgroundColor: "transparent",
          borderColor: (theme) =>
            theme.palette.mode === "dark" ? "#f44336" : "#d32f2f",
          color: (theme) =>
            theme.palette.mode === "dark" ? "#f44336" : "#d32f2f",
        },
      };
    case "Uncategorized":
      return {
        sx: {
          backgroundColor: (theme) =>
            theme.palette.mode === "dark" ? "#424242" : "#9e9e9e",
          color: "#fff",
        },
      };
    default:
      return {
        sx: {
          backgroundColor: "transparent",
          borderColor: (theme) =>
            theme.palette.mode === "dark" ? "#757575" : "#9e9e9e",
          color: (theme) =>
            theme.palette.mode === "dark" ? "#757575" : "#9e9e9e",
        },
      };
  }
};

const getLocationChipColor = (location) => {
  if (location === "USA") return "primary";
  if (location === "Europe") return "secondary";
  if (location === "Asia") return "warning";
  if (location === "Africa") return "success";
  return "default";
};

const isPopulatedQueryId = (queryId) =>
  queryId && typeof queryId === 'object' && queryId.link != null;

const scrollableTextSx = {
  background: "#f6f6f600",
  borderRadius: 1,
  p: 2,
  fontFamily: "monospace",
  whiteSpace: "pre-wrap",
  maxHeight: 350,
  overflow: "auto",
};

const LinkDetailsDialog = ({
  open,
  onClose,
  link,
  loading,
  onResearchClick,
  getUserNameById,
  getRelativeTimeString,
  formatQueryDateLimit,
  strictlyFilteredJobs = false,
  canEdit = false,
  techStackOptions = [],
  onLinkUpdated,
}) => {
  const [researchDialogOpen, setResearchDialogOpen] = useState(false);
  const [researchCompanyName, setResearchCompanyName] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editLocation, setEditLocation] = useState('Anonymous');
  const [editTechStack, setEditTechStack] = useState('N/A');
  const [editBehavior, setEditBehavior] = useState('');
  const [detailLink, setDetailLink] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const displayLink = detailLink ?? link;
  const isLoading = loading;

  const resolvedLocation = displayLink?.location || displayLink?.final_details?.jobContext?.location || 'Anonymous';
  const resolvedTechStack = displayLink?.techStack || displayLink?.final_details?.jobContext?.techStack || 'N/A';
  const resolvedTechStackList = Array.isArray(displayLink?.techStackList)
    ? displayLink.techStackList
    : Array.isArray(displayLink?.final_details?.jobContext?.techStackList)
      ? displayLink.final_details.jobContext.techStackList
      : Array.isArray(displayLink?.final_details?.customizationTarget?.techStackList)
        ? displayLink.final_details.customizationTarget.techStackList
        : [];
  const resolvedBehavior = displayLink?.final_details?.tag || '';
  const applicationEmail = displayLink?.final_details?.applicationMethods?.applicationEmail || '';
  const scrapedJobDescription = displayLink?.final_details?.jobContext?.jobDescription?.trim() || '';
  const showApplicationEmail =
    resolvedBehavior === 'Email Found' ||
    Boolean(applicationEmail) ||
    displayLink?.final_details?.applicationMethods?.isApplicationEmailFound;

  const stackChoices = techStackOptions.length > 0
    ? [...new Set([...techStackOptions.map((o) => o.value || o.label).filter((v) => v && v !== 'all'), 'N/A'])]
    : ['N/A'];

  useEffect(() => {
    if (!displayLink) return;
    setEditLocation(resolvedLocation);
    setEditTechStack(resolvedTechStack);
    setEditBehavior(resolvedBehavior);
    setIsEditing(false);
  }, [displayLink, open, resolvedLocation, resolvedTechStack, resolvedBehavior]);

  useEffect(() => {
    if (!open || !link?._id || loading) {
      setDetailLink(null);
      setDetailLoading(false);
      return undefined;
    }

    let cancelled = false;
    setDetailLoading(true);

    axios
      .get(`${process.env.REACT_APP_API_URL}/bid-links/${link._id}`)
      .then((response) => {
        if (!cancelled && response.data) {
          const fetched = response.data;
          setDetailLink({
            ...fetched,
            queryId: isPopulatedQueryId(fetched.queryId)
              ? fetched.queryId
              : (isPopulatedQueryId(link.queryId) ? link.queryId : fetched.queryId),
            queryDateLimit: fetched.queryDateLimit ?? link.queryDateLimit,
          });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDetailLink(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setDetailLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, link?._id, loading]);

  const handleOpenResearch = (companyName) => {
    setResearchCompanyName(companyName || '');
    setResearchDialogOpen(true);
  };

  const handleSaveEdits = async () => {
    if (!link?._id) return;

    setSaving(true);
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/bid-links/${link._id}`,
        {
          location: editLocation,
          techStack: editTechStack,
          behavior: editBehavior,
        }
      );

      const updatedLink = {
        ...link,
        location: response.data.location ?? editLocation,
        techStack: response.data.techStack ?? editTechStack,
        final_details: {
          ...link.final_details,
          ...response.data.final_details,
          tag: response.data.final_details?.tag ?? editBehavior,
          jobContext: {
            ...link.final_details?.jobContext,
            ...response.data.final_details?.jobContext,
            location: response.data.location ?? editLocation,
            techStack: response.data.techStack ?? editTechStack,
          },
        },
      };

      onLinkUpdated?.(updatedLink);
      setDetailLink((prev) => (prev ? { ...prev, ...updatedLink } : prev));
      setIsEditing(false);
      toast.success('Link properties updated');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update link properties');
    } finally {
      setSaving(false);
    }
  };

  if (!link && !isLoading) return null;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Link Details
          {canEdit && displayLink && !isLoading && (
            <Button
              size="small"
              startIcon={<EditIcon />}
              onClick={() => setIsEditing((prev) => !prev)}
              disabled={saving}
            >
              {isEditing ? 'Cancel Edit' : 'Edit'}
            </Button>
          )}
        </DialogTitle>
        <DialogContent>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
              <Typography>Loading...</Typography>
            </Box>
          ) : (
            <List>
              <ListItem>
                <ListItemText primary="Title" secondary={displayLink.title || "N/A"} />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Original URL"
                  secondary={
                    <Link
                      href={stripTrackingParams(displayLink.url)}
                      target="_blank"
                      rel={EXTERNAL_LINK_REL}
                    >
                      {displayLink.url}
                    </Link>
                  }
                />
              </ListItem>
              {displayLink.final_details?.finalUrl && (
                <ListItem>
                  <ListItemText
                    primary="Final URL"
                    secondary={
                      <Link
                        href={stripTrackingParams(displayLink.final_details.finalUrl)}
                        target="_blank"
                        rel={EXTERNAL_LINK_REL}
                      >
                        {displayLink.final_details.finalUrl}
                      </Link>
                    }
                  />
                </ListItem>
              )}
              <ListItem>
                <ListItemText
                  primary="Company"
                  secondary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">
                        {displayLink.company || "N/A"}
                      </Typography>
                      {displayLink.company && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<SearchIcon />}
                          onClick={() => handleOpenResearch(displayLink.company)}
                          sx={{
                            minWidth: 'auto',
                            px: 1,
                            py: 0.5,
                            fontSize: '0.75rem'
                          }}
                        >
                          Research
                        </Button>
                      )}
                    </Box>
                  }
                />
              </ListItem>

              <ListItem>
                <ListItemText
                  primary="Location"
                  secondary={
                    isEditing ? (
                      <FormControl size="small" fullWidth sx={{ mt: 0.5 }}>
                        <InputLabel>Location</InputLabel>
                        <Select
                          value={editLocation}
                          label="Location"
                          onChange={(e) => setEditLocation(e.target.value)}
                        >
                          {LOCATION_OPTIONS.map((loc) => (
                            <MenuItem key={loc} value={loc}>{loc}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Chip
                          label={resolvedLocation}
                          size="small"
                          color={getLocationChipColor(resolvedLocation)}
                          variant="outlined"
                        />
                      </Box>
                    )
                  }
                />
              </ListItem>

              <ListItem>
                <ListItemText
                  primary="Job Behavior"
                  secondary={
                    isEditing ? (
                      <FormControl size="small" fullWidth sx={{ mt: 0.5 }}>
                        <InputLabel>Job Behavior</InputLabel>
                        <Select
                          value={editBehavior}
                          label="Job Behavior"
                          onChange={(e) => setEditBehavior(e.target.value)}
                          displayEmpty
                        >
                          <MenuItem value="">
                            <em>None</em>
                          </MenuItem>
                          {BEHAVIOR_OPTIONS.map((behavior) => (
                            <MenuItem key={behavior} value={behavior}>{behavior}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : resolvedBehavior ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Chip
                          label={resolvedBehavior}
                          size="small"
                          sx={{
                            ...getTagColor(resolvedBehavior).sx,
                          }}
                        />
                      </Box>
                    ) : (
                      'N/A'
                    )
                  }
                />
              </ListItem>

              {showApplicationEmail && (
                <ListItem>
                  <ListItemText
                    primary="Application Email"
                    secondary={
                      applicationEmail ? (
                        <Link href={`mailto:${applicationEmail}`}>
                          {applicationEmail}
                        </Link>
                      ) : (
                        'N/A'
                      )
                    }
                  />
                </ListItem>
              )}

              {scrapedJobDescription ? (
                <ListItem sx={{ display: 'block', py: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Scraped Job Description
                  </Typography>
                  <Typography variant="body2" sx={scrollableTextSx}>
                    {scrapedJobDescription}
                  </Typography>
                </ListItem>
              ) : detailLoading && resolvedBehavior === 'Email Found' ? (
                <ListItem>
                  <ListItemText
                    primary="Scraped Job Description"
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <CircularProgress size={16} />
                        <Typography variant="body2" color="text.secondary">
                          Loading...
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ) : null}

              <ListItem>
                <ListItemText
                  primary="Tech Stack"
                  secondary={
                    isEditing ? (
                      <FormControl size="small" fullWidth sx={{ mt: 0.5 }}>
                        <InputLabel>Tech Stack</InputLabel>
                        <Select
                          value={editTechStack}
                          label="Tech Stack"
                          onChange={(e) => setEditTechStack(e.target.value)}
                        >
                          {stackChoices.map((stack) => (
                            <MenuItem key={stack} value={stack}>{stack}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Chip
                          label={resolvedTechStack}
                          size="small"
                          color={resolvedTechStack !== "N/A" ? "info" : "default"}
                          variant="outlined"
                        />
                      </Box>
                    )
                  }
                />
              </ListItem>

              {resolvedTechStackList.length > 0 && (
                <ListItem>
                  <ListItemText
                    primary="JD Technologies"
                    secondary={
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {resolvedTechStackList.map((tech) => (
                          <Chip
                            key={tech}
                            label={tech}
                            size="small"
                            color="info"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    }
                  />
                </ListItem>
              )}

              <ListItem>
                <ListItemText
                  primary="Description"
                  secondary={displayLink.description || "N/A"}
                  secondaryTypographyProps={{
                    style: { whiteSpace: "pre-wrap" },
                  }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Posted Date"
                  secondary={formatLinkPostedDate(displayLink.date, getRelativeTimeString) || "N/A"}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Indexed By"
                  secondary={
                    displayLink.created_by && getUserNameById
                      ? getUserNameById(displayLink.created_by) || "Unknown"
                      : "Unknown"
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Indexed"
                  secondary={
                    displayLink.created_at && getRelativeTimeString
                      ? getRelativeTimeString(new Date(displayLink.created_at))
                      : displayLink.created_at
                      ? new Date(displayLink.created_at).toLocaleString()
                      : "N/A"
                  }
                />
              </ListItem>
              {displayLink.queryId && (
                <>
                  <ListItem>
                    <ListItemText
                      primary="Query Used"
                      secondary={displayLink.queryId.link}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Query Date Limit"
                      secondary={formatQueryDateLimit ? formatQueryDateLimit(displayLink.queryDateLimit) : "N/A"}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Category"
                      secondary={displayLink.queryId.category || "N/A"}
                    />
                  </ListItem>
                </>
              )}
              {displayLink.confidence && (
                <>
                  <ListItem>
                    <ListItemText
                      primary="Confidence"
                      secondary={displayLink.confidence || "N/A"}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Reason"
                      secondary={displayLink.reason || "N/A"}
                    />
                  </ListItem>
                </>
              )}

              {displayLink.final_details?.explanation && (
                <Accordion sx={{ mt: 2 }}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="details-content"
                    id="details-header"
                  >
                    <Typography variant="subtitle1" fontWeight="bold">
                      Page Analysis
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" sx={scrollableTextSx}>
                      {displayLink.final_details.explanation}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              )}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          {isEditing && (
            <Button
              onClick={handleSaveEdits}
              variant="contained"
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          )}
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <CompanyResearchDialog
        open={researchDialogOpen}
        onClose={() => setResearchDialogOpen(false)}
        companyName={researchCompanyName}
      />
    </>
  );
};

export default LinkDetailsDialog;
